import { useState, useEffect } from 'react';
import { Loader2, Inbox, Send, AlertTriangle, Check } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../../../foundation/altzero_base/base/components/ui/dialog';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import { billEmailService, type BillEmailAccount } from '../../services/billEmailService';

type MailboxModeDialogProps = {
  account: BillEmailAccount | null;
  onClose: () => void;
  onSaved: () => void;
};

type Mode = 'both' | 'send_only';

/**
 * What a connected mailbox is actually used for.
 *
 * Replaces a "Send only" button that flipped a flag behind a `window.confirm`.
 * Two things were wrong with that: "send only" describes the *result*, not the
 * choice being made, and the consequence that matters — nobody reads replies to
 * a send-only mailbox — was buried in a browser confirm nobody reads either.
 *
 * Here both options are laid out with what each one does, the current one is
 * shown as current, and the warning appears the moment "send only" is picked —
 * before saving, not after.
 */
export const MailboxModeDialog = ({ account, onClose, onSaved }: MailboxModeDialogProps) => {
  const current: Mode = account?.poll_inbox === false ? 'send_only' : 'both';
  const [mode, setMode] = useState<Mode>(current);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMode(account?.poll_inbox === false ? 'send_only' : 'both');
    setError(null);
  }, [account?.id, account?.poll_inbox]);

  if (!account) return null;

  const changed = mode !== current;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const res = await billEmailService.setPollInbox(account.id, mode === 'both');
    setSaving(false);
    if (!res.success) {
      setError(res.error ?? 'Could not update this mailbox');
      return;
    }
    onSaved();
    onClose();
  };

  return (
    <Dialog open={!!account} onOpenChange={(next) => { if (!next && !saving) onClose(); }}>
      <DialogContent data-testid="mailbox-mode-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base">Mailbox settings</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-gray-500 -mt-1 break-all">{account.email}</p>

        <div className="space-y-2.5 mt-2">
          <ModeOption
            selected={mode === 'both'}
            isCurrent={current === 'both'}
            onSelect={() => setMode('both')}
            icon={<Inbox className="h-4 w-4" />}
            title="Send and receive"
            description="Bill validation reports go out from this mailbox, and invoices arriving in it are picked up automatically."
            recommended
          />

          <ModeOption
            selected={mode === 'send_only'}
            isCurrent={current === 'send_only'}
            onSelect={() => setMode('send_only')}
            icon={<Send className="h-4 w-4" />}
            title="Send only"
            description="Reports still go out from this mailbox, but nothing arriving in it is read. Use this for a mailbox that is not an invoice inbox."
          />
        </div>

        {mode === 'send_only' && (
          <div className="flex gap-2 items-start rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 mt-1">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-900">
              <strong>Replies to this mailbox will not be seen.</strong> Reports are sent from a bill
              validation mailbox so that a client replying to one lands somewhere the inbox is
              watching. Nothing arriving here will be read by anyone.
            </p>
          </div>
        )}

        {error && (
          <p data-testid="mailbox-mode-error" className="text-xs text-red-600 mt-1">{error}</p>
        )}

        <p className="text-[11px] text-gray-400 mt-1">
          Invoices already picked up from this mailbox are unaffected either way.
        </p>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button data-testid="mailbox-mode-save" onClick={() => void handleSave()} disabled={saving || !changed} className="gap-1.5">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {changed ? 'Save changes' : 'No changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

type ModeOptionProps = {
  selected: boolean;
  isCurrent: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  title: string;
  description: string;
  recommended?: boolean;
};

const ModeOption = ({
  selected, isCurrent, onSelect, icon, title, description, recommended,
}: ModeOptionProps) => (
  <button
    type="button"
    onClick={onSelect}
    className={cn(
      'w-full text-left rounded-xl border p-3.5 transition-colors flex gap-3',
      selected ? 'border-blue-400 bg-blue-50/60 ring-1 ring-blue-200' : 'border-gray-200 hover:border-gray-300 bg-white',
    )}
  >
    <span className={cn(
      'h-8 w-8 rounded-full flex items-center justify-center shrink-0',
      selected ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500',
    )}>
      {icon}
    </span>

    <span className="min-w-0 flex-1">
      <span className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold text-gray-900">{title}</span>
        {recommended && (
          <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
            Recommended
          </span>
        )}
        {isCurrent && (
          <span className="text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
            Current
          </span>
        )}
      </span>
      <span className="block text-xs text-gray-500 mt-1 leading-relaxed">{description}</span>
    </span>

    {selected && <Check className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />}
  </button>
);
